/**
 * DraftEditor — edición inline de un borrador de email (Fase 58).
 *
 * Renderiza los campos editables del draft (destinatario, asunto, cuerpo)
 * y un toggle "No enviar". Los cambios se auto-guardan con debounce de 2s
 * llamando a updateDraft().
 */

import { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { updateDraft } from "../../platform/api.ts";
import type { EmailDraft } from "../../platform/api.ts";

export type DraftEditorProps = {
  processingId: string;
  draft: EmailDraft;
  onDraftUpdated: (updated: EmailDraft) => void;
};

export function DraftEditor({ processingId, draft, onDraftUpdated }: DraftEditorProps) {
  const [to, setTo] = useState(draft.to);
  const [toName, setToName] = useState(draft.toName);
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [excluded, setExcluded] = useState(draft.excluded ?? false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  // Sync with external draft changes
  useEffect(() => {
    setTo(draft.to);
    setToName(draft.toName);
    setSubject(draft.subject);
    setBody(draft.body);
    setExcluded(draft.excluded ?? false);
  }, [draft.id]);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  const save = useCallback(
    async (fields: Record<string, unknown>) => {
      setSaving(true);
      setSaveError(null);
      try {
        const { draft: updated } = await updateDraft(
          processingId,
          draft.id,
          fields,
        );
        if (mountedRef.current) {
          onDraftUpdated(updated);
          setSaving(false);
        }
      } catch (err) {
        if (mountedRef.current) {
          setSaveError(err instanceof Error ? err.message : String(err));
          setSaving(false);
        }
      }
    },
    [processingId, draft.id, onDraftUpdated],
  );

  const debouncedSave = useCallback(
    (fields: Record<string, unknown>) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void save(fields);
      }, 2000);
    },
    [save],
  );

  const handleToChange = (value: string) => {
    setTo(value);
    debouncedSave({ to: value });
  };

  const handleToNameChange = (value: string) => {
    setToName(value);
    debouncedSave({ toName: value });
  };

  const handleSubjectChange = (value: string) => {
    setSubject(value);
    debouncedSave({ subject: value });
  };

  const handleBodyChange = (value: string) => {
    setBody(value);
    debouncedSave({ body: value });
  };

  const handleExcludedToggle = () => {
    const next = !excluded;
    setExcluded(next);
    // Excluded toggle saves immediately (no debounce) — it's a decision
    void save({ excluded: next });
  };

  return (
    <EditorContainer>
      {saving && <SavingIndicator>Guardando…</SavingIndicator>}
      {saveError && <SaveError>Error: {saveError}</SaveError>}

      <FieldGroup>
        <FieldLabel>Para (nombre)</FieldLabel>
        <Input
          value={toName}
          onChange={(e) => handleToNameChange(e.target.value)}
          placeholder="Nombre del destinatario"
        />
      </FieldGroup>

      <FieldGroup>
        <FieldLabel>Email</FieldLabel>
        <Input
          value={to}
          onChange={(e) => handleToChange(e.target.value)}
          placeholder="email@ejemplo.com"
          type="email"
        />
      </FieldGroup>

      <FieldGroup>
        <FieldLabel>Asunto</FieldLabel>
        <Input
          value={subject}
          onChange={(e) => handleSubjectChange(e.target.value)}
          placeholder="Asunto del email"
        />
      </FieldGroup>

      <FieldGroup>
        <FieldLabel>Cuerpo</FieldLabel>
        <BodyTextarea
          value={body}
          onChange={(e) => handleBodyChange(e.target.value)}
          placeholder="Cuerpo del email"
          rows={Math.max(6, body.split("\n").length + 2)}
        />
      </FieldGroup>

      <ExcludedRow>
        <ExcludedLabel>
          <ExcludedCheckbox
            type="checkbox"
            checked={excluded}
            onChange={handleExcludedToggle}
          />
          No enviar este correo
        </ExcludedLabel>
        {excluded && <ExcludedBadge>Excluido</ExcludedBadge>}
      </ExcludedRow>
    </EditorContainer>
  );
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
  padding-top: ${({ theme }) => theme.space.sm};
  border-top: 1px solid ${({ theme }) => theme.color.border};
  margin-top: ${({ theme }) => theme.space.sm};
`;

const SavingIndicator = styled.div`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.accent};
  font-style: italic;
`;

const SaveError = styled.div`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.error};
  background: ${({ theme }) => theme.color.errorMuted};
  padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.sm};
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const FieldLabel = styled.label`
  font-size: ${({ theme }) => theme.font.size.xs};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.color.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Input = styled.input`
  font-family: ${({ theme }) => theme.font.family};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  background: ${({ theme }) => theme.color.background};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.sm};
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.color.accent};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.color.accentMuted};
  }
`;

const BodyTextarea = styled.textarea`
  font-family: ${({ theme }) => theme.font.family};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  background: ${({ theme }) => theme.color.background};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.sm};
  outline: none;
  resize: vertical;
  line-height: 1.5;

  &:focus {
    border-color: ${({ theme }) => theme.color.accent};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.color.accentMuted};
  }
`;

const ExcludedRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
`;

const ExcludedLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  cursor: pointer;
`;

const ExcludedCheckbox = styled.input`
  cursor: pointer;
`;

const ExcludedBadge = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  color: ${({ theme }) => theme.color.warning};
  background: ${({ theme }) => theme.color.warningMuted};
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
`;
